import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { useDropdownPortal } from '@/hooks/useDropdownPortal';

export interface SimpleSelectOption {
  value: string;
  label: string;
  /** Renders greyed out and cannot be picked — the equivalent of `<option disabled>`. */
  disabled?: boolean;
  /** Group heading this option belongs under — the equivalent of `<optgroup label>`. */
  group?: string;
}

interface SimpleSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SimpleSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Classes applied to the trigger button. */
  className?: string;
  /** Classes applied to the wrapper (use for sizing in flex/grid parents, e.g. `w-full min-w-0`). */
  wrapperClassName?: string;
  id?: string;
  /** Accessible name when there is no visible <label> pointing at `id`. */
  'aria-label'?: string;
  /** Compact trigger for dense contexts (table cells, header pills). Defaults to 'md' — today's size. */
  size?: 'sm' | 'md';
  /** Extra content pinned below the option list, e.g. an inline "Custom value" input. Call `close()` to dismiss the menu (same as picking an option). */
  footer?: (close: () => void) => React.ReactNode;
  /** Fires whenever the menu transitions from open to closed, for any reason (option picked, footer's close(), outside click, Escape). Use to reset footer-local state (e.g. a half-typed custom input) so it doesn't linger stale on reopen. */
  onClose?: () => void;
}

/**
 * Lightweight, viewport-aware replacement for a native <select>. The menu renders
 * in a portal and flips/clamps to stay on screen, so it never overflows the viewport
 * on mobile the way the browser's native select dropdown does.
 *
 * Because it replaces a native control, it reimplements the keyboard behaviour the
 * browser would otherwise give for free: Arrow keys move the active option, Enter /
 * Space commits, Home/End jump to the ends, Escape closes, and typing jumps to the
 * first option whose label starts with what you typed.
 */
export default function SimpleSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  wrapperClassName = '',
  id,
  'aria-label': ariaLabel,
  size = 'md',
  footer,
  onClose,
}: SimpleSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const { triggerRef, menuRef, dropdownPos, updatePosition, isOutside } = useDropdownPortal(isOpen);

  const selected = options.find((o) => o.value === value);

  // Indices that can actually be landed on by the keyboard / clicked.
  const selectableIndexes = useMemo(
    () => options.reduce<number[]>((acc, o, i) => (o.disabled ? acc : [...acc, i]), []),
    [options]
  );

  // Group headers are only rendered when at least one option asks for one, so
  // ungrouped lists stay visually identical to before.
  const hasGroups = useMemo(() => options.some((o) => o.group), [options]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) onClose?.();
    wasOpenRef.current = isOpen;
  }, [isOpen, onClose]);

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const commit = useCallback(
    (index: number) => {
      const opt = options[index];
      if (!opt || opt.disabled) return;
      // A native <select> fires no change event when you re-pick the current
      // value; match that so re-selecting doesn't reset pagination/filters.
      if (opt.value !== value) onChange(opt.value);
      close();
    },
    [options, value, onChange, close]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (isOutside(e)) close();
    };
    // Escape is handled at the document level (so it still works when focus has
    // moved off the trigger, e.g. into a `footer` input) and in the CAPTURE
    // phase with propagation stopped, so an enclosing modal's own Escape
    // handler doesn't also fire — the browser's native select popup swallowed
    // Escape the same way.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [isOpen, isOutside, close]);

  // Keep the active option scrolled into view as the arrow keys move it.
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    menuRef.current
      ?.querySelector<HTMLElement>(`[data-option-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, activeIndex, menuRef]);

  // Type-ahead buffer — mirrors the native select's "type a few letters to jump".
  const typeaheadRef = useRef({ term: '', at: 0 });
  const typeahead = useCallback(
    (char: string) => {
      const now = performance.now();
      const buf = typeaheadRef.current;
      buf.term = now - buf.at > 700 ? char : buf.term + char;
      buf.at = now;

      const match = selectableIndexes.find((i) =>
        options[i].label.toLowerCase().startsWith(buf.term.toLowerCase())
      );
      if (match === undefined) return;
      if (isOpen) setActiveIndex(match);
      else commit(match);
    },
    [selectableIndexes, options, isOpen, commit]
  );

  const openMenu = useCallback(
    (startAt?: 'first' | 'last' | 'selected') => {
      if (disabled) return;
      updatePosition();
      const currentIndex = options.findIndex((o) => o.value === value);
      const first = selectableIndexes[0] ?? -1;
      const last = selectableIndexes[selectableIndexes.length - 1] ?? -1;
      setActiveIndex(
        startAt === 'last' ? last : startAt === 'first' ? first : currentIndex >= 0 ? currentIndex : first
      );
      setIsOpen(true);
    },
    [disabled, updatePosition, options, value, selectableIndexes]
  );

  const handleTriggerClick = () => {
    if (disabled) return;
    if (isOpen) close();
    else openMenu('selected');
  };

  const step = (delta: number) => {
    const pos = selectableIndexes.indexOf(activeIndex);
    const nextPos = pos < 0 ? (delta > 0 ? 0 : selectableIndexes.length - 1) : pos + delta;
    const clamped = Math.max(0, Math.min(selectableIndexes.length - 1, nextPos));
    setActiveIndex(selectableIndexes[clamped] ?? -1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu('selected');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        openMenu('last');
      } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        typeahead(e.key);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        step(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        step(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(selectableIndexes[0] ?? -1);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(selectableIndexes[selectableIndexes.length - 1] ?? -1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        commit(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          typeahead(e.key);
        }
    }
  };

  const sizeClasses = size === 'sm' ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm';
  const listboxId = id ? `${id}-listbox` : undefined;

  return (
    <div ref={triggerRef} className={`relative ${wrapperClassName}`}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={ariaLabel}
        className={`w-full ${sizeClasses} border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`}
      >
        <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 ml-2 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          style={
            dropdownPos.dropUp
              ? { bottom: dropdownPos.bottom, left: dropdownPos.left, width: dropdownPos.width }
              : { top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }
          }
        >
          <div id={listboxId} role="listbox" className="max-h-60 overflow-y-auto dropdown-scrollbar py-1">
            {options.map((opt, i) => {
              const isSelected = value === opt.value;
              const isActive = activeIndex === i;
              // Only print a heading when the group actually changes.
              const showHeading = hasGroups && !!opt.group && opt.group !== options[i - 1]?.group;
              return (
                <div key={`${opt.value}-${i}`}>
                  {showHeading && (
                    <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {opt.group}
                    </div>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-option-index={i}
                    disabled={opt.disabled}
                    onClick={() => commit(i)}
                    onMouseEnter={() => !opt.disabled && setActiveIndex(i)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors ${
                      hasGroups && opt.group ? 'pl-5' : ''
                    } ${
                      opt.disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : isSelected
                          ? 'bg-indigo-50 text-indigo-700 font-medium'
                          : 'text-gray-700'
                    } ${isActive && !opt.disabled && !isSelected ? 'bg-gray-50' : ''}`}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && <Check className="w-4 h-4 flex-shrink-0 ml-2" />}
                  </button>
                </div>
              );
            })}
          </div>
          {footer && (
            <div className="border-t border-gray-200">
              {footer(close)}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
